import type { ImageLoader } from 'next/image'

/**
 * imgproxy integration — off-server AVIF/WebP delivery.
 *
 * Instead of running the `next/image` optimizer (Sharp) on the app server for
 * every request, images are served through a central imgproxy instance that
 * reads the original from S3 and encodes on-the-fly (AVIF/WebP negotiated via
 * the `Accept` header). Sharp then only runs once at upload time (Payload).
 *
 * The loader is only active when BOTH env vars are set. When unset,
 * `imgproxyEnabled` is `false` and every helper falls back to the original
 * media URL so the on-server optimizer keeps working (safe gradual rollout).
 *
 *   NEXT_PUBLIC_IMGPROXY_URL=https://imgproxy.<domain>
 *   NEXT_PUBLIC_IMGPROXY_BUCKET=s3://<bucket>
 *
 * NOTE: `NEXT_PUBLIC_*` is baked into the client bundle at `next build` time.
 * In Dokploy these must be set as BUILD env, otherwise the loader stays
 * disabled and the on-server optimizer keeps running.
 *
 * See: https://github.com/morgendigital/payload-skills/blob/main/image-optimization/description.md
 */

const strip = (s: string) => s.replace(/\/+$/, '')

const ENDPOINT = strip((process.env.NEXT_PUBLIC_IMGPROXY_URL ?? '').trim())
const BUCKET = strip((process.env.NEXT_PUBLIC_IMGPROXY_BUCKET ?? '').trim())

const DEFAULT_QUALITY = 80

/** `true` when imgproxy is configured (both env vars present at build time). */
export const imgproxyEnabled = Boolean(ENDPOINT && BUCKET)

/** Turn an absolute origin URL into a same-origin relative path (used for SVGs). */
const sameOrigin = (u: string): string => {
  try {
    if (/^https?:\/\//i.test(u)) {
      const x = new URL(u)
      return `${x.pathname}${x.search}`
    }
  } catch {
    /* noop */
  }
  return u
}

/**
 * Extract the S3 key (Payload `filename`) from a media URL.
 * Handles absolute URLs, `/api/media/file/<key>` paths and bare filenames,
 * strips any query string (cache tag) and decodes percent-encoding.
 */
export const mediaKeyFromUrl = (url?: string | null): string => {
  if (!url) return ''
  let path = url
  try {
    if (/^https?:\/\//i.test(url)) path = new URL(url).pathname
  } catch {
    /* noop */
  }
  path = path.split('?')[0] ?? ''
  const marker = '/api/media/file/'
  const i = path.indexOf(marker)
  const seg = i >= 0 ? path.slice(i + marker.length) : (path.split('/').pop() ?? '')
  try {
    return decodeURIComponent(seg)
  } catch {
    return seg
  }
}

const buildUrl = (key: string, o: { width: number; quality?: number }): string =>
  `${ENDPOINT}/insecure/rs:fit:${o.width}/q:${o.quality ?? DEFAULT_QUALITY}/plain/${BUCKET}/${encodeURIComponent(
    key.replace(/^\/+/, ''),
  )}`

/**
 * `next/image` loader — only usable inside Client Components. `src` is the S3
 * key (Payload `filename`), NOT `/api/media/file/...`.
 */
export const imgproxyLoader: ImageLoader = ({ src, width, quality }) =>
  buildUrl(src, { width, quality: quality ?? undefined })

/**
 * Build a finished imgproxy URL from a media URL (Server Components, raw `<img>`,
 * WebGL, `motion.img`). Give the result to `next/image` together with
 * `unoptimized` so it is not re-wrapped in `/_next/image`.
 *
 * When imgproxy is disabled the original URL is returned unchanged.
 * SVGs are never rasterised — they are returned as a same-origin path.
 */
export const imgproxyUrlFromMediaUrl = (
  u: string | null | undefined,
  o: { width: number; quality?: number },
): string => {
  if (!u) return ''
  if (!imgproxyEnabled) return u
  // Only route our own Payload/S3 media (`…/api/media/file/<key>`) through
  // imgproxy. External URLs (e.g. Unsplash) and static public assets are left
  // untouched so callers can pass any `src` safely.
  if (!u.includes('/api/media/file/')) return u
  const key = mediaKeyFromUrl(u)
  if (!key) return u
  if (key.toLowerCase().endsWith('.svg')) return sameOrigin(u)
  return buildUrl(key, o)
}
